import { useCallback, useMemo, useState } from 'react';
import { isEqual } from 'lodash-es';
import { FieldValues, UseFormReturn } from 'react-hook-form';

// A filter's value as the badge compares it: a date by its day (a default
// range is built from `new Date()` at a different time than the applied one),
// an array item by item, an empty value -- an empty array, a range of empty
// ends included -- as the empty string.
const comparable = (value: unknown): unknown => {
  if (value instanceof Date) return value.toDateString();
  if (Array.isArray(value)) {
    const items = value.map(comparable);
    return items.every((item) => item === '') ? '' : items;
  }
  return value ?? '';
};

/**
 * The filters button's badge (N1, brief pattern B): how many of `fields` have
 * an applied value that is not their default.
 */
export const countActiveFilters = <T extends object>(
  applied: Partial<T>,
  defaults: Partial<T>,
  fields: readonly (keyof T & string)[],
) => fields.filter((key) => !isEqual(comparable(applied[key]), comparable(defaults[key]))).length;

// N2c: a page form's default is often an ISO date string (a grid config's
// `options.value`, a store's initial filters) while the form writes a Date --
// read as a date, the two compare by day like two Dates.
const ISO_DATE = /^\d{4}-\d{2}-\d{2}T/;
const asDate = (value: unknown): unknown => {
  if (typeof value === 'string' && ISO_DATE.test(value)) {
    return new Date(value);
  }
  if (Array.isArray(value)) {
    return value.map(asDate);
  }
  return value;
};
const pickDates = <T extends object>(values: Partial<T>, fields: readonly (keyof T & string)[]) =>
  Object.fromEntries(fields.map((key) => [key, asDate(values[key])])) as Partial<T>;

/**
 * N2c (PageFilters): the badge of a page's own filter form -- how many of
 * `fields` have an applied value that is not the page's default, an ISO date
 * string compared as a date.
 */
export const countPageFilters = <T extends object>(
  applied: Partial<T>,
  defaults: Partial<T>,
  fields: readonly (keyof T & string)[],
) => countActiveFilters(pickDates(applied, fields), pickDates(defaults, fields), fields);

// Every one of `fields` from `values`, one that `values` leaves out included
// (as undefined): a grid drops its empty filters, and a draft value must not
// outlive the draft.
const pickAll = <T extends object>(values: Partial<T>, fields: readonly (keyof T & string)[]) =>
  Object.fromEntries(fields.map((key) => [key, values[key]])) as Partial<T>;

interface Props<T extends FieldValues> {
  /** The useFilterForm result: the draft is its form state. */
  form: Pick<UseFormReturn<T>, 'reset' | 'getValues'>;
  /** The applied values (the form's `defaultValues`). */
  applied: T;
  /** What "clear" puts the dialog's fields back to: the list's defaults. */
  cleared: Partial<T>;
  /** The fields that live in the dialog (a stable array). */
  fields: readonly (keyof T & string)[];
  /** Commits the form: useFilterForm's `onCheckAndUpdate`. */
  onApply: () => void;
}

/**
 * N1 (brief pattern B): the draft behind a useFilterForm screen's
 * FiltersDialog. Below md the dialog's fields edit the form without applying
 * it (their onChange leaves `onCheckAndUpdate` out); "apply" commits the form
 * and closes, "clear" puts the dialog's fields back to `cleared` in the draft,
 * closing puts them back to the applied values. `count` is the number of the
 * dialog's fields whose applied value is not `cleared` -- the FiltersButton's
 * badge. A field outside the dialog (the search, at rest) is never touched.
 */
export const useFiltersDialog = <T extends FieldValues>({
  form,
  applied,
  cleared,
  fields,
  onApply,
}: Props<T>) => {
  const { reset, getValues } = form;
  const [isOpen, setOpen] = useState(false);

  const open = useCallback(() => setOpen(true), []);
  const close = useCallback(() => {
    reset({ ...getValues(), ...pickAll(applied, fields) });
    setOpen(false);
  }, [reset, getValues, applied, fields]);
  const apply = useCallback(() => {
    onApply();
    setOpen(false);
  }, [onApply]);
  const clear = useCallback(() => {
    reset({ ...getValues(), ...pickAll(cleared, fields) });
  }, [reset, getValues, cleared, fields]);

  const count = useMemo(
    () => countActiveFilters(applied, cleared, fields),
    [fields, applied, cleared],
  );

  return { isOpen, open, close, apply, clear, count };
};
